import React, { useState, useEffect } from 'react';
import { Download, Users, Search } from 'lucide-react';
import { formatDateForInput, todayStr, daysAgoStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import * as XLSX from 'xlsx';

const CustomerRankingByProductTypeComponent = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerNames, setCustomerNames] = useState({});
  const [customerPhones, setCustomerPhones] = useState({});
  const [loadingNames, setLoadingNames] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState('all');
  const [dateRange, setDateRange] = useState(() => ({
    start_date: daysAgoStr(30),
    end_date: todayStr(),
  }));

  const productTypes = ['SAT', 'Solar', 'MTF', 'MTB', 'Other'];

  const fetchCustomerName = async (customerCode) => {
    if (customerNames[customerCode] || !customerCode) {
      return customerNames[customerCode];
    }

    // Special case for cash customer
    if (customerCode === '5890045') {
      setCustomerNames(prev => ({ ...prev, [customerCode]: 'เงินสด' }));
      setCustomerPhones(prev => ({ ...prev, [customerCode]: '-' }));
      return 'เงินสด';
    }

    try {
      const requestBody = {
        code: String(customerCode).trim()
      };
      
      const response = await fetch('https://apisaleeasy.psisat.com/api/technician/by-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status && result.data) {
          const firstName = result.data.technician_name || result.data.name || result.data.customer_name || result.data.first_name || '';
          const lastName = result.data.technician_surname || result.data.surname || result.data.last_name || result.data.customer_surname || '';
          const name = `${firstName}${lastName ? ' ' + lastName : ''}`.trim() || 'ไม่ระบุ';
          const phone = result.data.technician_phone || result.data.phone || result.data.customer_phone || result.data.mobile || '-';

          setCustomerNames(prev => ({ ...prev, [customerCode]: name }));
          setCustomerPhones(prev => ({ ...prev, [customerCode]: phone }));
          
          return name;
        }
      }
    } catch (err) {
      console.error(`Error fetching customer name for ${customerCode}:`, err);
    }
    
    return 'ไม่ระบุ';
  };

  const fetchAllCustomerNames = async (customers) => {
    if (!customers || customers.length === 0) return;
    
    setLoadingNames(true);
    
    try {
      const uniqueCustomerCodes = [...new Set(customers.map(c => c.customer_code))];
      const customersToLoad = uniqueCustomerCodes.filter(code => !customerNames[code]);
      
      // Process customers one by one with updates
      for (let i = 0; i < customersToLoad.length; i++) {
        const customerCode = customersToLoad[i];
        await fetchCustomerName(customerCode);
        
        // Add delay to prevent overwhelming the API and allow UI updates
        await new Promise(resolve => setTimeout(resolve, 150));
      }
    } catch (err) {
      console.error('Error fetching customer names:', err);
    } finally {
      setLoadingNames(false);
    }
  };

  const processInvoiceDataByProductType = (invoices) => {
    const customerTotals = {};
    const productTypeTotals = {
      'SAT':   0,
      'Solar': 0,
      'MTF':   0,
      'MTB':   0,
      'Other': 0
    };

    // Process each invoice
    invoices.forEach(invoice => {
      const customerId = invoice.customer_code;
      const customerName = invoice.customer_name || `ลูกค้า ${customerId}`;

      if (!customerTotals[customerId]) {
        customerTotals[customerId] = {
          customer_id: customerId,
          customer_name: customerName,
          product_type_breakdown: {
            'SAT':   0,
            'Solar': 0,
            'MTF':   0,
            'MTB':   0,
            'Other': 0
          },
          total_amount: 0
        };
      }

      // Process line items if they exist
      if (invoice.line_items && Array.isArray(invoice.line_items)) {
        invoice.line_items.forEach(item => {
          const amount = parseFloat(item.amount || 0);
          let productType = 'Other'; // default

          // Determine product type based on product name or code
          const productName = (item.product_name || '').toLowerCase();
          const productCode = (item.product_code || '').toLowerCase();
          
          if (productName.includes('sat') || productCode.includes('sat')) {
            productType = 'SAT';
          } else if (productName.includes('solar') || productCode.includes('solar')) {
            productType = 'Solar';
          } else if (productCode.includes('sol-mtf')) {
            productType = 'MTF';
          } else if (productCode.includes('sol-mtb')) {
            productType = 'MTB';
          }

          customerTotals[customerId].product_type_breakdown[productType] += amount;
          customerTotals[customerId].total_amount += amount;
          productTypeTotals[productType] += amount;
        });
      } else {
        // If no line items, treat entire invoice as 'Other'
        const amount = parseFloat(invoice.total_amount || 0);
        customerTotals[customerId].product_type_breakdown['Other'] += amount;
        customerTotals[customerId].total_amount += amount;
        productTypeTotals['Other'] += amount;
      }
    });

    // Convert to array format
    const customers = Object.values(customerTotals);

    return {
      customers,
      product_type_totals: productTypeTotals
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const requestBody = {
        date_range: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date
        },
        filters: {
          company_code: "045"
        }
      };

      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/customer-ranking-cached', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
        
      if (result.data) {
        setData({
          customers: result.data.customers,
          product_type_totals: result.data.summary?.product_type_totals || {}
        });
        setError(null);
        
        // Fetch customer names in background after showing the data
        if (result.data.customers) {
          // Don't await - let it run in background
          fetchAllCustomerNames(result.data.customers);
        }
      } else {
        setError('ไม่พบข้อมูลลูกค้าในช่วงเวลาที่เลือก');
      }
    } catch (err) {
      console.error('❌ API Error:', err);
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getRankedCustomersByProductType = (productType) => {
    if (!data || !data.customers) {
      return [];
    }
    
    const processedCustomers = data.customers
      .map(customer => {
        const productTypeAmount = customer.product_type_breakdown?.[productType]?.total_amount || 0;
        return {
          ...customer,
          customer_name: customer.customer_name || `ลูกค้า ${customer.customer_code}`,
          productTypeTotal: productTypeAmount
        };
      })
      .filter(customer => customer.productTypeTotal > 0)
      .sort((a, b) => b.productTypeTotal - a.productTypeTotal)
      .map((customer, index) => ({
        ...customer,
        rank: index + 1
      }));
    
    return processedCustomers;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const exportToExcel = () => {
    if (!data || loadingNames) return;

    const wb = XLSX.utils.book_new();

    productTypes.forEach(productType => {
      const rankedCustomers = getRankedCustomersByProductType(productType);
      // Only export top 10 customers
      const top10Customers = rankedCustomers.slice(0, 10);
      const worksheetData = [
        ['อันดับ', 'รหัสลูกค้า', 'ชื่อลูกค้า', 'เบอร์โทร', `ยอดซื้อ ${productType} (บาท)`],
        ...top10Customers.map(customer => [
          customer.rank,
          customer.customer_code,
          customerNames[customer.customer_code] || 'ไม่ระบุ',
          customerPhones[customer.customer_code] || '-',
          customer.productTypeTotal
        ])
      ];

      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(wb, ws, `อันดับ ${productType}`);
    });

    XLSX.writeFile(wb, `อันดับลูกค้าตามประเภทสินค้า_Top10_${dateRange.start_date}_${dateRange.end_date}.xlsx`);
  };

  const ProductTypeTable = ({ productType }) => {
    const rankedCustomers = getRankedCustomersByProductType(productType);
    const total = data?.product_type_totals?.[productType] || 0;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">
            อันดับลูกค้า - {productType}
          </h3>
          <div className="text-sm text-gray-600">
            ยอดรวม: {formatCurrency(total)}
          </div>
        </div>
        
        {rankedCustomers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">อันดับ</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">รหัสลูกค้า</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">ชื่อลูกค้า</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">เบอร์โทร</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">ยอดซื้อ (บาท)</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">สัดส่วน (%)</th>
                </tr>
              </thead>
              <tbody>
                {rankedCustomers.slice(0, 10).map((customer) => (
                  <tr key={customer.customer_id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        customer.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                        customer.rank === 2 ? 'bg-gray-100 text-gray-800' :
                        customer.rank === 3 ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {customer.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {customer.customer_code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {customerNames[customer.customer_code] ? (
                        customerNames[customer.customer_code]
                      ) : loadingNames ? (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-gray-500 text-xs">กำลังโหลด...</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">ไม่ระบุ</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {customerPhones[customer.customer_code] ? (
                        customerPhones[customer.customer_code]
                      ) : loadingNames ? (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-gray-500 text-xs">กำลังโหลด...</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(customer.productTypeTotal)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {total > 0 ? ((customer.productTypeTotal / total) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            ไม่มีข้อมูลสำหรับประเภทสินค้า {productType}
          </div>
        )}
      </div>
    );
  };

  const handleSearch = () => {
    fetchData();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">เกิดข้อผิดพลาด: {error}</p>
          <button
            onClick={fetchData}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Users className="h-6 w-6 text-purple-600 mr-3" />
                <h1 className="text-xl font-semibold text-gray-900">
                  อันดับลูกค้าตามประเภทสินค้า
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">วันที่เริ่มต้น:</label>
                  <MaskedDatePicker
                    value={dateRange.start_date}
                    onChange={(str) => setDateRange({...dateRange, start_date: str})}
                    placeholderText="DD/MM/YYYY"
                    className="border rounded px-3 py-1 text-sm"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">วันที่สิ้นสุด:</label>
                  <MaskedDatePicker
                    value={dateRange.end_date}
                    onChange={(str) => setDateRange({...dateRange, end_date: str})}
                    placeholderText="DD/MM/YYYY"
                    className="border rounded px-3 py-1 text-sm"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  ค้นหา
                </button>
                <button
                  onClick={exportToExcel}
                  disabled={!data || loadingNames}
                  className="flex items-center bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingNames ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      รอโหลดข้อมูล...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Export Excel (Top 10)
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {data && (
              <div className="flex items-center space-x-3 pt-4 border-t">
                <span className="text-sm font-medium text-gray-700">แสดงประเภท:</span>
                <button
                  onClick={() => setSelectedProductType('all')}
                  className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                    selectedProductType === 'all' 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  ทั้งหมด
                </button>
                {productTypes.map(productType => (
                  <button
                    key={productType}
                    onClick={() => setSelectedProductType(productType)}
                    className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                      selectedProductType === productType 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {productType}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        ) : !data ? (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">อันดับลูกค้าตามประเภทสินค้า</h3>
            <p className="text-gray-600 mb-6">เลือกช่วงวันที่แล้วกดปุ่มค้นหาเพื่อดูข้อมูล</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-blue-800 text-sm">
                💡 ระบบจะแสดงอันดับลูกค้าแยกตาม 5 ประเภทสินค้า: SAT, Solar, MTF (โรงงาน), MTB (นำเข้า), และ Other
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {selectedProductType === 'all' ? (
              productTypes.map(productType => (
                <ProductTypeTable key={productType} productType={productType} />
              ))
            ) : (
              <ProductTypeTable productType={selectedProductType} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerRankingByProductTypeComponent;